<template>
  <Transition name="fade">
    <div v-if="closeSync && isExtension"
         class="fixed h-screen m-0 flex items-center justify-center z-50 w-full left-0 top-0 bg-black bg-opacity-40" :style="{zIndex: dialogZIndex}">
      <div class="bg-white p-4 rounded-xl w-11/12">
        <div class="flex items-center justify-between">
          <div class="flex flex-col py-2">
            <WcTypography size="md" weight="600"  :title="title" class="jtitle" />
            <WcTypography size="xs" weight="400" :title="description" />
          </div>
          <svg  data-testid="close-dialog" width="16" height="16" viewBox="0 0 16 16" class="cursor-pointer" @click="handleDialogClose()" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <path
                d="M8.00084 6.35117L13.7758 0.576172L15.4255 2.22584L9.6505 8.00084L15.4255 13.7758L13.7758 15.4255L8.00084 9.6505L2.22584 15.4255L0.576172 13.7758L6.35117 8.00084L0.576172 2.22584L2.22584 0.576172L8.00084 6.35117Z"
                fill="#040410" fill-opacity="0.87" />
          </svg>
        </div>
        <div class="mb-3">
          <slot name="content"></slot>
        </div>
        <slot name="footer"></slot>
      </div>
    </div>
    <div v-else-if="closeSync && bodyScroll"
         class="fixed h-screen m-0 flex items-center justify-center w-full left-0 top-0 bg-black bg-opacity-40 jdialog" :style="{zIndex: dialogZIndex}">
      <div class="md:max-w-xl flex flex-col lg:max-w-2xl xl:max-w-5xl 2xl:max-w-5xl mx-auto bg-white rounded-xl jModelContainerScroll overflow-hidden"
           :style=" {'min-width': minWidth}"
      >
        <div class="flex justify-between items-center px-4 py-3" :class="{'border-b': hasScrolled}">
          <div class="flex flex-col gap-1">
            <WcTypography size="md" weight="600" :title="title" class="jtitle" />
            <WcTypography v-if="description" size="sm" weight="400" :title="description" color="rgba(4, 4, 16, 0.58)" />
          </div>
          <div class="flex items-center gap-x-4">
            <slot name="prefixItems"></slot>
            <div class="hover:bg-gray-100 rounded-lg cursor-pointer close-icon py-1 px-3" @click="handleDialogClose()">
              <WcIconCross height="13" width="13" />
            </div>
          </div>
        </div>
        <div class="overflow-y-auto scroll-w-1 px-4 py-3" @scroll="handleScroll">
          <slot name="content"></slot>
        </div>
        <div :class="`${$slots.showFooter ? 'px-4 py-3': 'pb-2'} ${footerBorder ? 'border-t': ''}`">
          <slot name="showFooter"></slot>
        </div>
      </div>
    </div>
    <div v-else-if="closeSync && !isMobile()"
         class="fixed h-screen m-0 flex items-center justify-center w-full left-0 top-0 bg-black bg-opacity-40 jdialog" :style="{zIndex: dialogZIndex}">
      <div class="md:max-w-xl overflow-y-auto lg:max-w-2xl xl:max-w-5xl 2xl:max-w-5xl mx-auto bg-white rounded-xl jModalContainer"
           :style=" {'min-width': minWidth}"
      >
        <div class="flex justify-between">
          <div class="flex flex-col py-2 gap-1">
            <WcTypography size="md" weight="600"  :title="title" class="jtitle" />
            <WcTypography size="sm" weight="400" :title="description" color="rgba(4, 4, 16, 0.58)" />
          </div>
          <div class="flex items-center gap-x-6">
            <slot name="prefixItems"></slot>
            <svg data-testid="close-dialog" width="16" height="16" viewBox="0 0 16 16" class="cursor-pointer" @click="handleDialogClose()" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.00084 6.35117L13.7758 0.576172L15.4255 2.22584L9.6505 8.00084L15.4255 13.7758L13.7758 15.4255L8.00084 9.6505L2.22584 15.4255L0.576172 13.7758L6.35117 8.00084L0.576172 2.22584L2.22584 0.576172L8.00084 6.35117Z"
                fill="#040410" fill-opacity="0.87" />
            </svg>
          </div>
        </div>
        <div class="jbody">
          <slot name="content"></slot>
        </div>
        <slot name="footer"></slot>
      </div>
    </div>
    <div v-else-if="closeSync"
         class="fixed h-screen m-0 flex items-center justify-center w-full left-0 top-0 bg-black bg-opacity-40" :style="{zIndex: dialogZIndex}">
      <div class="bg-white p-4 rounded-xl w-11/12">
        <div class="flex items-center justify-between">
          <div class="flex flex-col py-2">
            <WcTypography size="md" weight="600"  :title="title" class="jtitle" />
            <WcTypography size="xs" weight="400" :title="description" />
          </div>
          <svg width="16" height="16" viewBox="0 0 16 16" class="cursor-pointer" @click="handleDialogClose()" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.00084 6.35117L13.7758 0.576172L15.4255 2.22584L9.6505 8.00084L15.4255 13.7758L13.7758 15.4255L8.00084 9.6505L2.22584 15.4255L0.576172 13.7758L6.35117 8.00084L0.576172 2.22584L2.22584 0.576172L8.00084 6.35117Z"
              fill="#040410" fill-opacity="0.87" />
          </svg>
        </div>
        <div class="mb-3">
          <slot name="content"></slot>
        </div>
        <slot name="footer"></slot>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { isMobile, randomId } from '@/utils/general'
import WcTypography from '@/components/UILibrary/Typography/WcTypography.vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import WcIconCross from '@/components/UILibrary/Icons/WcIconCross.vue'
import Tooltip from '@/components/UILibrary/tooltip.vue'

@Component({
  name: 'WcDialog',
  methods: { isMobile },
  components: { Tooltip, WcIconCross, WcTypography }
})
export default class WcDialog extends Vue {
  @Prop({ default: false, required: true }) closeSync!: boolean
  @Prop({ required: true, type: String, default: '' }) title!: string
  @Prop({ required: false, type: String }) description!: string
  @Prop({ default: false, required: false }) isExtension!: boolean
  @Prop({ default: false, required: false }) bodyScroll!: boolean
  @Prop({ default: '40rem', required: false }) minWidth!: string
  @Prop({ default: false }) footerBorder!: boolean

  private DialogManager = {
    /** Counter to track the total number of dialogs created */
    counter: 0,
    /** Set to store currently active dialog IDs */
    activeDialogs: new Set<number>(),
    /** Base z-index value for dialogs. All dialogs will stack above this value */
    BASE_Z_INDEX: 1500,

    /**
     * Registers a new dialog and returns its unique ID.
     * Each new dialog gets an incrementing ID to manage stacking order.
     * @returns {number} Unique ID for the dialog
     */
    register(): number {
      this.counter++
      this.activeDialogs.add(this.counter)
      return this.counter
    },

    /**
     * Unregisters a dialog when it's closed or destroyed.
     * @param {number} id - The ID of the dialog to unregister
     */
    unregister(id: number): void {
      this.activeDialogs.delete(id)
    },

    /**
     * Calculates the z-index for a dialog based on its ID.
     * Later dialogs get higher z-index values to appear on top.
     * @param {number} dialogId - The ID of the dialog
     * @returns {number} The calculated z-index value
     */
    getDialogZIndex(dialogId: number): number {
      return this.BASE_Z_INDEX + (dialogId * 2) - 1
    }
  }

  private dialogEl: HTMLElement | null = null

  private dialogId: number = 0;

  /**
   * Computed property that returns the z-index for this dialog instance.
   * Uses DialogManager to calculate proper stacking order.
   *
   * @returns {number} The z-index value for this dialog
   *
   * Example:
   * - First dialog: z-index = 1500 + (1 * 2) = 1502
   * - Second dialog: z-index = 1500 + (2 * 2) = 1504
   * - Third dialog: z-index = 1500 + (3 * 2) = 1506
   *
   * Usage in template:
   * <div :style="{ zIndex: dialogZIndex }">
   */
  get dialogZIndex(): number {
    return this.DialogManager.getDialogZIndex(this.dialogId)
  }

  @Watch('closeSync')
  onCloseSyncChange(newVal: boolean): void {
    if (newVal) {
      // Dialog is being shown
      this.dialogId = this.DialogManager.register()
      this.$nextTick(() => {
        this.mountToBody()
      })
    } else {
      // Dialog is being hidden
      this.DialogManager.unregister(this.dialogId)
      this.unmountFromBody()
    }
  }

  private mountToBody() {
    if (!this.dialogEl) {
      this.dialogEl = document.createElement('div')
      this.dialogEl.id = `dialog-${randomId()}`
    }

    if (this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }

    this.dialogEl.appendChild(this.$el)
    document.body.appendChild(this.dialogEl)
  }

  private unmountFromBody() {
    if (this.dialogEl && document.body.contains(this.dialogEl)) {
      document.body.removeChild(this.dialogEl)
    }
  }

  mounted() {
    if (this.closeSync) {
      this.dialogId = this.DialogManager.register()
      this.$nextTick(() => {
        this.mountToBody()
      })
    }
  }

  beforeDestroy() {
    if (this.closeSync) {
      this.DialogManager.unregister(this.dialogId)
      this.unmountFromBody()
    }
  }

  handleDialogClose() {
    this.$emit('close-jdialog')
  }

  hasScrolled = false;
  handleScroll(event: Event): void {
    const target = event.target as HTMLElement
    this.hasScrolled = target.scrollTop > 0
  }
}
</script>

<style scoped>

h1 {
  margin: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.jtitle {
  color: #010113;
}

.jdialog {
  backdrop-filter: blur(7.5px);
}

.jModalContainer {
  max-height: 90vh;
  padding: 24px;
}

.jModelContainerScroll {
  max-height: 90vh;
}

.jbody {
  margin-top: 16px;
  margin-bottom: 32px;
}

.py-3{
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}

.px-3{
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

i {
  margin: 0;
}

.scroll-w-1::-webkit-scrollbar {
  width: 4px;
}

</style>
